import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import DonorAccess from './DonorAccess';
import RichTextEditor from '../../../components/RichTextEditor';

const EditDonorModal = ({ donorId, isOpen, onClose, onSuccess, onUpdateDonor, onAccessChange }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  const [donor, setDonor] = useState(null);
  const [activeTab, setActiveTab] = useState('info'); // 'info' или 'access'
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    tags: '',
    description: '',
    weight: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Загружаем данные донора при открытии модального окна
  useEffect(() => {
    if (isOpen && donorId) {
      fetchDonor();
    }
  }, [isOpen, donorId]);

  // Обновляем данные формы при изменении донора
  useEffect(() => {
    if (donor) {
      setFormData({
        url: donor.url || '',
        title: donor.title || '',
        tags: donor.tags || '',
        description: donor.description || '',
        weight: donor.weight || 0
      });
      setError(null);
    }
  }, [donor]);

  const fetchDonor = async () => {
    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-donors/${donorId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setDonor(data.data);
      } else {
        throw new Error(data.message || 'Ошибка загрузки донора');
      }
    } catch (error) {
      console.error('Ошибка загрузки донора:', error);
      setError(error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // Функция показа уведомлений
  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-donors/${donorId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем данные донора в модальном окне
        setDonor(data.data);
        // Обновляем данные формы
        setFormData({
          url: data.data.url || '',
          title: data.data.title || '',
          tags: data.data.tags || '',
          description: data.data.description || '',
          weight: data.data.weight || 10
        });
        setError(null);
        // Показываем уведомление об успехе
        showNotification(
          currentLang === 'ru' ? 'Донор успешно обновлен!' : 'Donor updated successfully!',
          'success'
        );
        // Обновляем данные в основном списке
        if (onUpdateDonor && donorId) {
          onUpdateDonor(donorId, data.data);
        }
        
        // Закрываем модальное окно сразу
        onClose();
        onSuccess();
      } else {
        setError(data.message || 'Ошибка обновления донора');
      }
    } catch (error) {
      console.error('Ошибка обновления донора:', error);
      setError(error.message || 'Ошибка сети при обновлении донора');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">
              {currentLang === 'ru' ? 'Редактировать донора' : 'Edit Donor'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Табы */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('info')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'info'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {currentLang === 'ru' ? 'Информация' : 'Information'}
              </button>
              <button
                onClick={() => setActiveTab('access')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'access'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {currentLang === 'ru' ? 'Доступ' : 'Access'}
              </button>
            </nav>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Контент табов */}
          {activeTab === 'info' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'URL донора' : 'Donor URL'} *
                </label>
                <input
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={currentLang === 'ru' ? 'https://example.com' : 'https://example.com'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Название' : 'Title'}
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={currentLang === 'ru' ? 'Введите название донора' : 'Enter donor title'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Теги' : 'Tags'}
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={currentLang === 'ru' ? 'seo, outreach, guest post' : 'seo, outreach, guest post'}
                />
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="weight"
                    checked={formData.weight > 0}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {currentLang === 'ru' ? 'Закрепить донора' : 'Pin donor'}
                  </span>
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 9V4h1V2H7v2h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1l1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"/>
                  </svg>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {currentLang === 'ru' ? 'Закрепленные доноры отображаются вверху списка' : 'Pinned donors appear at the top of the list'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Описание' : 'Description'}
                </label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                  currentLang={currentLang}
                  placeholder={currentLang === 'ru' ? 'Введите описание донора...' : 'Enter donor description...'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Статус' : 'Status'}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value={1}>{currentLang === 'ru' ? 'Активный' : 'Active'}</option>
                  <option value={0}>{currentLang === 'ru' ? 'Неактивный' : 'Inactive'}</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.url.trim()}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{currentLang === 'ru' ? 'Сохранение...' : 'Saving...'}</span>
                    </div>
                  ) : (
                    currentLang === 'ru' ? 'Сохранить' : 'Save'
                  )}
                </button>
                {loading && (
                  <div className="text-sm text-gray-500 mt-2 text-center">
                    {currentLang === 'ru' ? 'Обновляем данные...' : 'Updating data...'}
                  </div>
                )}
              </div>
            </form>
          ) : (
            <div>
              {donor && (
                <DonorAccess 
                  donor={donor} 
                  onAccessChange={() => {
                    // Обновляем данные донора после изменения доступа
                    if (donorId) {
                      fetchDonor();
                    }
                    // Уведомляем родительский компонент
                    if (onAccessChange) {
                      onAccessChange(donorId);
                    }
                  }}
                />
              )}
              <div className="flex justify-end pt-4">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {currentLang === 'ru' ? 'Закрыть' : 'Close'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditDonorModal; 